import { apiErrorSchema, type ApiError } from '@warehouse/shared';

/**
 * Every non-2xx response from the api is `{ code, params? }` where `code` is an
 * i18n key (ARCHITECTURE §4, apps/api/src/core/errors/app-error.filter.ts).
 * This is the ONLY error type the app throws for a failed request, so a caller
 * never has to branch on `Response`/`TypeError`/`SyntaxError`.
 */
export class ApiRequestError extends Error {
  readonly code: string;
  readonly params: Record<string, unknown> | undefined;
  readonly status: number;
  /** True when the request never reached the server (offline, DNS, abort). */
  readonly isNetworkError: boolean;

  constructor(opts: {
    code: string;
    status: number;
    params?: Record<string, unknown> | undefined;
    isNetworkError?: boolean;
    cause?: unknown;
  }) {
    super(opts.code, opts.cause === undefined ? undefined : { cause: opts.cause });
    this.name = 'ApiRequestError';
    this.code = opts.code;
    this.params = opts.params;
    this.status = opts.status;
    this.isNetworkError = opts.isNetworkError ?? false;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

export function isApiRequestError(value: unknown): value is ApiRequestError {
  return value instanceof ApiRequestError;
}

/**
 * `E_NETWORK` is client-only — it is deliberately NOT in the shared
 * `ERROR_CODES` list (the server can never emit it). The shell catalog carries
 * the translation.
 */
export const CLIENT_ERROR_CODES = {
  network: 'E_NETWORK',
  unknown: 'E_INTERNAL',
} as const;

/**
 * Parse a failed `Response` body into the shared error envelope. Anything that
 * is not the envelope (an nginx HTML 502, an empty body) collapses onto a
 * status-derived code so the UI still shows a localized sentence.
 */
export async function toApiError(response: Response): Promise<ApiRequestError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) {
    const envelope: ApiError = parsed.data;
    return new ApiRequestError({
      code: envelope.code,
      params: envelope.params,
      status: response.status,
    });
  }

  return new ApiRequestError({
    code: statusToCode(response.status),
    status: response.status,
  });
}

function statusToCode(status: number): string {
  switch (status) {
    case 400:
      return 'E_VALIDATION';
    case 401:
      return 'E_UNAUTHORIZED';
    case 403:
      return 'E_FORBIDDEN';
    case 404:
      return 'E_NOT_FOUND';
    case 409:
      return 'E_CONFLICT';
    case 413:
      return 'E_FILE_TOO_LARGE';
    case 429:
      return 'E_RATE_LIMITED';
    default:
      return status >= 500 ? 'E_INTERNAL' : 'E_VALIDATION';
  }
}
